// stores/auth.ts
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { set } from "vue-gtag";
import type { LoginResponse } from "@/gen/v1/user_pb";
import { useClients } from "@/services/clients";
import { captureExceptionWithTrace } from "@/utils/sentry";
import { collectClientInfo } from "@/utils/clientInfo";
import { navigate } from "@/services/navigation";
import { isRetryableError } from "@/services/retryPolicy";
import { useSnackbarStore } from "@/stores/snackbarStore";
import { Code, ConnectError } from "@connectrpc/connect";
import { create } from "@bufbuild/protobuf";
import {
  LoginRequestSchema,
  RefreshTokenRequestSchema,
  LogoutRequestSchema,
} from "@/gen/v1/user_pb";
import { formatProtoDate } from "@/utils/dateFormatter";
import { EXPIRY_WARNING_DAYS, daysUntilExpiry } from "@/utils/expiry";

export const useAuthStore = defineStore("auth", () => {
  const { userClient } = useClients();

  const snackbar = useSnackbarStore();
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const userData = ref<LoginResponse | null>(
    localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user")!)
      : null,
  );

  // Reactive variable to control app bar and menu visibility
  const showAppBarMenu = ref<boolean>(!!localStorage.getItem("token"));

  // Computed username from userData
  const username = computed(() => userData.value?.username || "");

  // Single refresh lock to prevent concurrent refresh requests
  let refreshPromise: Promise<boolean> | null = null;

  /**
   * True from the moment an explicit logout starts until the next login.
   *
   * `logout` clears the refresh token, so a request that was still in flight
   * fails with Unauthenticated and reaches the refresh path with nothing left
   * to refresh. Without this flag that is indistinguishable from a genuine
   * expiry, and the user who just logged out is told their session expired.
   */
  let loggedOut = false;

  /**
   * True from the moment a session is ended until a new one starts.
   *
   * Requests that were in flight when the session ended are all about to fail,
   * and callers cannot tell why from the error alone: the Unauthenticated code
   * the auth interceptor throws does not survive the Connect transport, which
   * hands the caller a generic Unknown. This flag is the store stating plainly
   * what it did, so those callers can stay quiet instead of burying the
   * session-expired warning under their own error messages.
   */
  const sessionEnded = ref<boolean>(false);

  // Getter methods - always read from localStorage (source of truth)
  const getAccessToken = (): string | null => {
    return localStorage.getItem("token");
  };

  const getRefreshToken = (): string | null => {
    return localStorage.getItem("refreshToken");
  };
  // Get role from userData (from login/refresh response) or default to "user"
  const userRole = computed(() => {
    return userData.value?.role || "user";
  });

  const isAdmin = computed(() => userRole.value === "admin");

  const isShowCloseToExpiredMessage = computed(() => {
    const daysLeft = daysUntilExpiry(userData.value?.purchaseExpiry);
    if (daysLeft === null) return false;

    return daysLeft <= EXPIRY_WARNING_DAYS;
  });

  /** The expiry date as the header shows it (DD/MM/YYYY), "" when unset. */
  const purchaseExpiryText = computed(() =>
    formatProtoDate(userData.value?.purchaseExpiry),
  );

  /**
   * Whether the expiry is close enough to need the user's attention.
   *
   * There is no "expired" value: an expired account cannot log in, so the only
   * question left for a signed-in user is how much time is left.
   */
  const purchaseExpiryStatus = computed<"expiring" | "valid">(() =>
    isShowCloseToExpiredMessage.value ? "expiring" : "valid",
  );

  const login = async (
    loginUsername: string,
    loginPassword: string,
  ): Promise<void> => {
    try {
      error.value = null;
      isLoading.value = true;
      const response = await userClient.login(
        create(LoginRequestSchema, {
          username: loginUsername,
          password: loginPassword,
          clientInfo: await collectClientInfo(),
        }),
      );

      setToken(response);

      // Set VuetGtag user ID for tracking
      set({
        user_id: loginUsername,  // GA4 internal user identification
        username: loginUsername,  // Custom dimension for reports
      });
    } catch (err: unknown) {
      // Check if it's an account expired error
      const errorMessage = err instanceof Error ? err.message : String(err);
      const errorCode =
        err && typeof err === "object" && "code" in err
          ? (err as { code: string }).code
          : undefined;
      const rawMessage =
        err && typeof err === "object" && "rawMessage" in err
          ? (err as { rawMessage: string }).rawMessage
          : undefined;

      if (
        errorMessage?.includes("account expired") ||
        errorCode === "PERMISSION_DENIED" ||
        rawMessage?.includes("account expired")
      ) {
        error.value = "Tài khoản đã hết hạn sử dụng";
      } else {
        error.value = "Đăng nhập thất bại";
      }
      captureExceptionWithTrace(err, {
        extra: {
          username: loginUsername,
          context: "login",
          errorCode,
          rawMessage,
        },
      });
    } finally {
      isLoading.value = false;
    }
  };

  const setToken = (response: LoginResponse) => {
    // A new session: whatever the previous one ended with no longer applies.
    sessionEnded.value = false;
    loggedOut = false;
    snackbar.resumeErrors();

    // Write to localStorage (source of truth)
    localStorage.setItem("token", response.accessToken);
    localStorage.setItem("refreshToken", response.refreshToken);
    localStorage.setItem("username", response.username);
    localStorage.setItem("user", JSON.stringify(response));

    // Update refs for reactivity
    userData.value = response;
    // Don't set menu visibility here - let router guard handle it
  };

  const clearToken = () => {
    // Remove from localStorage (source of truth)
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("username");
    localStorage.removeItem("user");

    // Update refs for reactivity
    userData.value = null;
    // Don't hide menu here - let router guard handle visibility
  };

  const setShowAppBarMenu = (show: boolean) => {
    showAppBarMenu.value = show;
  };

  /**
   * Ends the session and sends the user back to the login screen.
   *
   * Called for every refresh failure the session cannot recover from. The
   * redirect is guarded because a router that is missing or rejects the
   * navigation must not turn `refreshAccessToken` into a rejected promise:
   * its callers rely on the `false` return to stop replaying the request.
   */
  const endSession = (): void => {
    clearToken();
    sessionEnded.value = true;
    snackbar.warning(
      "Phiên đăng nhập đã hết hạn hoặc đang sử dụng trên máy khác",
    );
    // Warn first, then go quiet: the requests that were in flight are all
    // about to fail, and their generic messages would replace this one. Not
    // every caller routes through useAsyncOperation - the onboarding stores
    // call the snackbar directly - so the guard belongs here, once.
    snackbar.suspendErrors();
    try {
      Promise.resolve(navigate("/login")).catch((err: unknown) => {
        captureExceptionWithTrace(err, {
          extra: { context: "endSession", stage: "navigate" },
        });
      });
    } catch (err: unknown) {
      captureExceptionWithTrace(err, {
        extra: { context: "endSession", stage: "navigate" },
      });
    }
  };

  const refreshAccessToken = async (): Promise<boolean> => {
    // If a refresh is already in progress, return the existing promise
    if (refreshPromise) {
      return refreshPromise;
    }

    // Create new refresh promise
    refreshPromise = (async () => {
      try {
        // Collect first: it may wait up to HINTS_TIMEOUT_MS, and the token
        // must be read right before the request so a rotation by another tab
        // during that wait is not lost.
        const clientInfo = await collectClientInfo();

        // Always read from localStorage (source of truth)
        const storedRefreshToken = getRefreshToken();

        if (!storedRefreshToken) {
          if (loggedOut) {
            // The user logged out; this is a request that was already in
            // flight, not an expiry. Fail it quietly - there is no session to
            // warn about and no screen to send them away from.
            return false;
          }

          // Nothing left to refresh with: the session is unrecoverable, so
          // clear what remains and get the user to the login screen instead
          // of leaving them on a page whose every request will fail.
          endSession();
          return false;
        }

        const response = await userClient.refreshToken(
          create(RefreshTokenRequestSchema, {
            refreshToken: storedRefreshToken,
            clientInfo,
          }),
        );

        setToken(response);

        // Update VuetGtag user ID for tracking
        set({
          user_id: username.value,  // GA4 internal user identification
          username: username.value,  // Custom dimension for reports
        });
        return true;
      } catch (err) {
        // Handle ConnectError from refreshToken call
        // Note: publicTransport no longer transforms errors, so we get the original ConnectError
        const isConnectError = err instanceof ConnectError;
        const errorCode = isConnectError ? err.code : Code.Unknown;

        // Anything the transport would not retry is terminal: the refresh can
        // never succeed again, so end the session. Relying on Unauthenticated
        // alone left the user stranded whenever the backend described the same
        // failure with another code.
        if (!isRetryableError(err)) {
          endSession();
        }
        // A retryable failure is a transient outage. The transport already
        // exhausted its retries, but the refresh token is still good, so keep
        // it and let the next request try again.

        captureExceptionWithTrace(err, {
          extra: {
            username: username.value,
            context: "refreshAccessToken",
            hasRefreshToken: !!getRefreshToken(),
            errorCode: errorCode,
            rawMessage: isConnectError ? err.rawMessage : undefined,
            details: isConnectError ? err.details : undefined,
          },
        });
        return false;
      } finally {
        // Clear the promise after completion (success or failure)
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };

  const logout = async (): Promise<void> => {
    // Recorded before the RPC: `finally` clears the token, and any request
    // still in flight then fails with Unauthenticated. This is what tells the
    // refresh path that the missing token is deliberate.
    loggedOut = true;

    try {
      // Call logout API to revoke refresh token on server
      // Username is extracted from JWT token on the server
      await userClient.logout(create(LogoutRequestSchema, {}));
    } catch (err) {
      // Continue with local logout even if server call fails
      captureExceptionWithTrace(err, {
        extra: {
          username: username.value,
          context: "logout",
        },
      });
    } finally {
      // Always clear tokens using clearToken (which handles localStorage)
      clearToken();
    }
  };

  return {
    username, // Computed from userData
    isLoading,
    error,
    getAccessToken, // Always reads from localStorage
    getRefreshToken, // Always reads from localStorage
    userData,
    userRole,
    isAdmin,
    sessionEnded, // True once a session has ended, until the next login
    showAppBarMenu, // Reactive variable for menu visibility
    setShowAppBarMenu, // Method to control menu visibility
    login,
    logout,
    setToken,
    refreshAccessToken,
    isShowCloseToExpiredMessage,
    purchaseExpiryText,
    purchaseExpiryStatus,
  };
});
